import settingsService from "app/pages/sales-intelligence/sub-modules/advanced-search/services/sw-settings/settingsService";
import type { ICountryObject } from "app/services/CountryService";
import { US_COUNTRY_ID, WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type { IRegionObject } from "app/services/RegionService";

type IPermittedRegionObject = IRegionObject & { permitted?: boolean };

export interface ISiCountryService {
    /** Company-information countries, excluding US states (they are offered separately as `usStateOptions`). */
    readonly allCountries: readonly ICountryObject[];
    /** Permitted countries first (last one flagged `isLastPermitted`), then the locked remainder. */
    readonly trafficCountries: readonly ICountryObject[];
    readonly trafficCountriesWithoutWorldwide: readonly ICountryObject[];
    readonly allRegions: readonly IPermittedRegionObject[];
    readonly usStateOptions: readonly ICountryObject[];
    readonly isWorldwideAvailable: boolean;
}

const getAllCountries = (): readonly ICountryObject[] =>
    settingsService.getCompanyInformationCountries().filter((country) => country.parent !== US_COUNTRY_ID);

const getTrafficCountries = (): readonly ICountryObject[] => {
    const permitted = settingsService.getCurrentComponentCountries();
    const lastPermittedIndex = permitted.length - 1;

    return [
        ...permitted.map((country, index) =>
            index === lastPermittedIndex ? { ...country, isLastPermitted: true } : country,
        ),
        ...settingsService.getCurrentComponentRestrictedCountries(),
    ];
};

/**
 * The SI-wide country authority: which countries this user may see, in which state (permitted or locked), for
 * the component they are currently on.
 *
 * Exposed as getters rather than a precomputed snapshot. Every value derives from `swSettings.current`, which
 * the router reassigns on each route transition, so a cached snapshot would go stale; reading on access keeps
 * the object identity constant (safe to hold in a build context) while the data stays current.
 */
export const siCountryService: ISiCountryService = {
    get allCountries() {
        return getAllCountries();
    },

    get usStateOptions() {
        return getAllCountries().find((country) => country.id === US_COUNTRY_ID)?.states ?? [];
    },

    get allRegions() {
        return settingsService.getRegions();
    },

    get trafficCountries() {
        return getTrafficCountries();
    },

    get trafficCountriesWithoutWorldwide() {
        return settingsService.getCountriesWithoutWW(getTrafficCountries());
    },

    get isWorldwideAvailable() {
        return settingsService.getCurrentComponentCountries().some(({ id }) => id === WORLDWIDE_COUNTRY_ID);
    },
};
